import { useCallback, useMemo } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { gql } from "@apollo/client";
import type { EventFilters } from "@/store/slices/eventsSlice";
import type { Event } from "@/types/domain";
import type { SquadApiData } from "@/lib/utils/squadBuilder";
import {
  CHECK_EVENT_ATTENDANCE_BY_USER,
  EMPLOYEES_IN_COMPANY,
  GET_ALL_EVENT_CONTENTS,
  GET_ALL_EVENT_TRANSACTION_HISTORY,
  GET_ALL_KEYWORD_CATEGORIES,
  GET_BILLING_COUPON_INFO_BY_PROMO_CODE,
  GET_EVENT,
  GET_EVENT_CART_ITEM,
  GET_EVENT_CLUSTERS,
  GET_EVENT_INVITATION,
  GET_LEAGUE_INVITATION_BY_TOKEN,
  GET_MY_LEAGUE_CLUB_STATUS,
  GET_EVENT_QR_PREFERENCES,
  GET_LINKED_LEAGUE_EVENT_ID,
  GET_CLUB_LEAGUE_CREDIT_OPTIONS,
  GET_TEAM_LEAGUE_SUBMISSION,
  GET_LEAGUE_ALLOCATION,
  GET_LEAGUE_FIXTURES,
  GET_CLUB_LEAGUE_FIXTURES,
  GET_PLATFORM_EVENT_MEMBERS,
  GET_PLATFORM_EVENTS,
  GET_PLATFORM_FAMILY_MEMBERS,
  GET_REQUEST_TO_JOIN_EVENT_LIST,
  GET_REQUEST_TO_JOIN,
  GET_CONTENT_CLUSTER_RECIPIENTS,
  GET_CONTENT_CLUSTER_RESPONSES,
  GET_SQUAD_BUILDER_DATA,
  MY_COMPANIES,
} from "../queries/events";
import {
  ADD_CONTENT_TO_CART,
  CREATE_EVENT_BOOKING,
  RESPOND_TO_CONTENT_SLOT,
  CONTENT_SLOT_RESPONSES,
  CONTENT_SLOT_BOOKINGS,
  PENDING_SLOT_DECLINE_REQUESTS,
  DECIDE_SLOT_DECLINE_REQUEST,
  CANCEL_SLOT_BOOKING,
  DELETE_EVENT_CART_ITEM,
  SUBMIT_RATING,
  UPDATE_CART_ITEM_QUANTITY,
  REQUEST_EVENT_ATTENDANCE,
  COMPLETE_LEAGUE_ONBOARDING,
  ACCEPT_LEAGUE_INVITATION,
  ISSUE_LEAGUE_INVITE_TOKEN,
  ADD_COMPANY,
  CREATE_EVENT,
  SET_LEAGUE_TEAM_ALLOCATION,
  GENERATE_FIXTURES,
  UPDATE_LEAGUE_FIXTURE,
  PUBLISH_LEAGUE_FIXTURES,
} from "../mutations/events";
import type {
  LeagueAllocationTeam,
  LeagueFixtureRecord,
} from "@/lib/utils/leagueFixtures";

// const CREATE_EVENT = gql`
//   mutation CreateEvent($input: CreateEventInput!) {
//     createEvent(input: $input) {
//       id
//       title
//       type
//       status
//       startsAt
//       endsAt
//     }
//   }
// `;

const UPDATE_EVENT = gql`
  mutation UpdateEvent($data: UpdateEventInput!) {
    updateEvent(data: $data)
  }
`;

const DELETE_EVENT = gql`
  mutation DeleteEvent($id: ID!) {
    deleteEvent(id: $id) {
      id
    }
  }
`;

// ─── Hook: paginated event list ───────────────────────────────────
interface UseEventsOptions {
  filters?: Partial<EventFilters>;
  pageSize?: number;
  /** Pass true for live events to enable polling */
  pollLive?: boolean;
}

export function useEvents({ filters }: UseEventsOptions = {}) {
  const {
    loading: singleLoading,
    error: singleError,
    refetch,
  } = useQuery<{
    getEvent: Event;
  }>(GET_EVENT, {
    variables: { eventID: (filters as any)?.eventID },
    skip: !(filters as any)?.eventID,
  });

  return {
    events: [],
    pageInfo: { hasNextPage: false, endCursor: null, totalCount: 1 } as any,
    loading: singleLoading,
    error: singleError,
    loadMore: () => {},
    refetch,
  };
}

export function usePlatformEventMembers(id: string, skip = false) {
  const { data, loading, error, refetch } = useQuery<{
    getPlatformEventMembers: any;
  }>(GET_PLATFORM_EVENT_MEMBERS, {
    variables: { id },
    skip: skip || !id,
    fetchPolicy: "network-only",
    errorPolicy: "none",
  });

  return {
    event: data?.getPlatformEventMembers ?? null,
    loading,
    error,
    refetch,
  };
}

export function usePlatformFamilyMembers(userID: string, eventID: string, skip = false) {
  const { data, loading, error, refetch } = useQuery<{
    familyMembersAttendingEvent: any;
  }>(GET_PLATFORM_FAMILY_MEMBERS, {
    variables: { userID, eventID },
    skip: skip || !userID || !eventID,
  });

  return {
    familyMembers: data?.familyMembersAttendingEvent ?? null,
    loading,
    error,
    refetch,
  };
}

export function useAllEventContents(eventId: string) {
  const { data, loading, error, refetch } = useQuery<{
    getAllEventContents: any;
  }>(GET_ALL_EVENT_CONTENTS, {
    variables: { eventId },
    skip: !eventId,
  });

  return {
    event: data?.getAllEventContents?.contents ?? null,
    loading,
    error,
    refetch,
  };
}

export function useAllEventTransactionHistory(
  eventId: string,
  forUser?: boolean,
) {
  const { data, loading, error, refetch } = useQuery<{
    getAllEventTransactionHistory: any;
  }>(GET_ALL_EVENT_TRANSACTION_HISTORY, {
    variables: { eventId, forUser },
    skip: !eventId,
  });

  return {
    history: data?.getAllEventTransactionHistory ?? null,
    loading,
    error,
    refetch,
  };
}

export function useEventCartItem(eventId: string) {
  const { data, loading, error, refetch } = useQuery<{
    getEventCartItem: any;
  }>(GET_EVENT_CART_ITEM, {
    variables: { eventId },
    skip: !eventId,
  });

  return {
    items: data?.getEventCartItem ?? null,
    loading,
    error,
    refetch,
  };
}

export function useSquadBuilderData(
  eventId: string,
  leagueEventId?: string,
  locale?: string,
) {
  const { data, loading, error, refetch } = useQuery<{
    getSquadBuilderData: SquadApiData;
  }>(GET_SQUAD_BUILDER_DATA, {
    variables: { data: { eventId, leagueEventId, locale } },
    skip: !eventId,
  });

  return {
    items: data?.getSquadBuilderData ?? null,
    loading,
    error,
    refetch,
  };
}

export interface ClubLeagueCreditOption {
  leagueEventId: string;
  leagueName: string | null;
  leagueSlug: string | null;
  leagueCredits: number;
  playerCredits: number;
  totalCredits: number;
}

export function useClubLeagueCreditOptions(clubEventId: string) {
  const { data, loading, error, refetch } = useQuery<{
    getClubLeagueCreditOptions: ClubLeagueCreditOption[];
  }>(GET_CLUB_LEAGUE_CREDIT_OPTIONS, {
    variables: { clubEventId },
    skip: !clubEventId,
    fetchPolicy: "cache-and-network",
  });

  return {
    options: data?.getClubLeagueCreditOptions ?? [],
    loading,
    error,
    refetch,
  };
}

export interface TeamLeagueSubmission {
  teamSubmitted: boolean;
  players: { playerId: string; status: string; creditUsed: boolean }[];
}

export function useTeamLeagueSubmission(
  clubEventId: string,
  leagueEventId: string,
  teamName: string,
) {
  const { data, loading, error, refetch } = useQuery<{
    getTeamLeagueSubmission: TeamLeagueSubmission;
  }>(GET_TEAM_LEAGUE_SUBMISSION, {
    variables: { clubEventId, leagueEventId, teamName },
    skip: !clubEventId || !leagueEventId || !teamName,
    fetchPolicy: "cache-and-network",
  });

  return {
    submission: data?.getTeamLeagueSubmission ?? null,
    loading,
    error,
    refetch,
  };
}

export function useLinkedLeagueEventId(clubEventId: string) {
  const { data, loading, error, refetch } = useQuery<{
    getLinkedLeagueEventId: any;
  }>(GET_LINKED_LEAGUE_EVENT_ID, {
    variables: { clubEventId },
    skip: !clubEventId,
  });

  return {
    linkedLeagueEventId: data?.getLinkedLeagueEventId ?? null,
    loading,
    error,
    refetch,
  };
}

export function useEventClusters(eventId: string) {
  const { data, loading, error, refetch } = useQuery<{
    getEventClusters: any;
  }>(GET_EVENT_CLUSTERS, {
    variables: { eventId },
    skip: !eventId,
  });

  return {
    clusters: data?.getEventClusters ?? null,
    loading,
    error,
    refetch,
  };
}

export function useBillingCouponInfoByPromoCode(
  promoCode: string,
  channel: string,
) {
  const { data, loading, error, refetch } = useQuery<{
    getBillingCouponInfoByPromoCode: any;
  }>(GET_BILLING_COUPON_INFO_BY_PROMO_CODE, {
    variables: { promoCode, channel },
    skip: !promoCode || !channel,
  });

  return {
    info: data?.getBillingCouponInfoByPromoCode ?? null,
    loading,
    error,
    refetch,
  };
}

// ─── Hook: create event ───────────────────────────────────────────
// export function useCreateEvent() {
//   const [mutate, { loading, error }] = useMutation(CREATE_EVENT, {
//     // Invalidate the events list so it refetches
//     refetchQueries: ["GetEvents"],
//   });

//   const createEvent = useCallback(
//     (input: Record<string, unknown>) => mutate({ variables: { input } }),
//     [mutate],
//   );

//   return { createEvent, loading, error };
// }

export function useCreateEventBooking() {
  const [mutate, { loading, error }] = useMutation(CREATE_EVENT_BOOKING);

  const createEventBooking = useCallback(
    (input: Record<string, any>) => {
      console.info("Creating event booking with input:", input);
      return mutate({ variables: input });
    },
    [mutate],
  );

  return { createEventBooking, loading, error };
}

// ─── Hook: update event ───────────────────────────────────────────
export function useUpdateEvent() {
  const [mutate, { loading, error }] = useMutation(UPDATE_EVENT, {
    errorPolicy: "none",
  });

  const updateEvent = useCallback(
    (id: string, input: Record<string, unknown>) =>
      mutate({
        variables: { data: { ...input, id, eventType: "PLATFORM_EVENT" } },
      }),
    [mutate],
  );

  return { updateEvent, loading, error };
}

// ─── Hook: delete event ───────────────────────────────────────────
export function useDeleteEvent() {
  // const client = useApolloClient();
  const [mutate, { loading, error }] = useMutation(DELETE_EVENT, {
    // Evict from cache immediately for snappy UI
    update(cache, { data }) {
      const deletedId = (data as { deleteEvent: { id: string } } | null)
        ?.deleteEvent?.id;

      if (!deletedId) return;

      const cacheId = cache.identify({
        __typename: "Event",
        id: deletedId,
      });

      if (!cacheId) return;

      if (deletedId) {
        cache.evict({
          id: cacheId,
        });
        cache.gc();
      }
    },
  });

  const deleteEvent = useCallback(
    (id: string) => mutate({ variables: { id } }),
    [mutate],
  );

  return { deleteEvent, loading, error };
}

export function useDeleteEventCartItem() {
  const [mutate, { loading, error }] = useMutation(DELETE_EVENT_CART_ITEM);

  const deleteEventCartItem = useCallback(
    (input: string) => {
      console.info("Deleting event cart item with input:", input);
      return mutate({ variables: { cartId: input } });
    },
    [mutate],
  );

  return { deleteEventCartItem, loading, error };
}

export function useUpdateEventCartItemQuantity() {
  const [mutate, { loading, error }] = useMutation(UPDATE_CART_ITEM_QUANTITY);

  const updateEventCartItemQuantity = useCallback(
    (input: Record<string, any>) => {
      console.info("Updating event cart item quantity with input:", input);
      return mutate({ variables: input });
    },
    [mutate],
  );

  return { updateEventCartItemQuantity, loading, error };
}

export function useEventAttendance(eventId: string) {
  const { data, loading, error, refetch } = useQuery<{
    checkEventAttendance: any;
  }>(CHECK_EVENT_ATTENDANCE_BY_USER, {
    variables: { eventID: eventId },
    skip: !eventId,
  });

  return {
    // Return null while loading/skipped so callers can distinguish
    // "not fetched yet" from a real empty/false value.
    attendance: data?.checkEventAttendance ?? null,
    loading,
    error,
    refetch,
  };
}

export function useRequestToJoinEventList(eventId: string) {
  const { data, loading, error, refetch } = useQuery<{
    getRequestToJoinEventList: any;
  }>(GET_REQUEST_TO_JOIN_EVENT_LIST, {
    variables: { eventId },
    skip: !eventId,
  });

  return {
    // Return null while loading/skipped so callers can distinguish
    // "not fetched yet" from an actual empty list and avoid false-negatives.
    requestToJoinEventList: data?.getRequestToJoinEventList ?? null,
    loading,
    error,
    refetch,
  };
}
export interface EventJoinState {
  isOrganiser: boolean;
  canManageRequests: boolean;
  hasAccess: boolean;
  requests: { id: string; requesterId: string; status: string }[];
}

export function useRequestToJoin(eventId: string, enabled = true) {
  const { data, loading, error, refetch } = useQuery<{
    getRequestToJoin: EventJoinState;
  }>(GET_REQUEST_TO_JOIN, {
    variables: { eventId },
    skip: !eventId || !enabled,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });
  return { joinState: data?.getRequestToJoin, loading, error, refetch };
}

export function useContentClusterRecipients(
  contentId: string,
  eventId: string,
) {
  const { data, loading, error, refetch } = useQuery<{
    getContentClusterRecipients: any;
  }>(GET_CONTENT_CLUSTER_RECIPIENTS, {
    variables: { data: { contentId, eventId } },
    skip: !contentId || !eventId,
  });

  return {
    recipients: data?.getContentClusterRecipients ?? null,
    loading,
    error,
    refetch,
  };
}

export function useContentClusterResponses(contentId: string, eventId: string) {
  const { data, loading, error, refetch } = useQuery<{
    getContentClusterResponses: any;
  }>(GET_CONTENT_CLUSTER_RESPONSES, {
    variables: { data: { contentId, eventId } },
    skip: !contentId || !eventId,
  });

  return {
    responses: data?.getContentClusterResponses ?? null,
    loading,
    error,
    refetch,
  };
}

// ─── Hook: per-slot response to content (CONFIRM books, DECLINE records only) ───
export function useRespondToContentSlot() {
  const [mutate, { loading, error }] = useMutation(RESPOND_TO_CONTENT_SLOT);

  const respondToContentSlot = useCallback(
    (data: Record<string, any>) => mutate({ variables: { data } }),
    [mutate],
  );

  return { respondToContentSlot, loading, error };
}

// Stored per-slot responses for the given users; memoised so effects keyed on it don't loop
export function useContentSlotResponses(input: { eventId: string; contentId: string; userIds: string[] }) {
  const skip = !input.eventId || !input.contentId || input.userIds.length === 0;
  const { data, loading, error, refetch } = useQuery(CONTENT_SLOT_RESPONSES, {
    variables: { data: input },
    skip,
    fetchPolicy: "network-only",
  });

  const responses = useMemo<any[]>(() => data?.contentSlotResponses ?? [], [data]);

  return { responses, loading, error, refetch };
}

// Active slot bookings for this content held by the given users
export function useContentSlotBookings(input: { eventId: string; contentId: string; userIds: string[] }) {
  const skip = !input.eventId || !input.contentId || input.userIds.length === 0;
  const { data, loading, error, refetch } = useQuery(CONTENT_SLOT_BOOKINGS, {
    variables: { data: input },
    skip,
    fetchPolicy: "network-only",
  });

  const bookings = useMemo<any[]>(() => data?.contentSlotBookings ?? [], [data]);

  return { bookings, loading, error, refetch };
}

// Paid-booking decline requests waiting for an admin decision
export function usePendingSlotDeclineRequests(eventId: string) {
  const { data, loading, error, refetch } = useQuery(PENDING_SLOT_DECLINE_REQUESTS, {
    variables: { eventId },
    skip: !eventId,
    fetchPolicy: "network-only",
  });

  const requests = useMemo<any[]>(() => data?.pendingSlotDeclineRequests ?? [], [data]);

  return { requests, loading, error, refetch };
}

export function useDecideSlotDeclineRequest() {
  const [mutate, { loading, error }] = useMutation(DECIDE_SLOT_DECLINE_REQUEST);

  const decideSlotDeclineRequest = useCallback(
    (responseId: string, approve: boolean) => mutate({ variables: { responseId, approve } }),
    [mutate],
  );

  return { decideSlotDeclineRequest, loading, error };
}

export function useCancelSlotBooking() {
  const [mutate, { loading, error }] = useMutation(CANCEL_SLOT_BOOKING);

  const cancelSlotBooking = useCallback(
    (slotId: string) => mutate({ variables: { slotId } }),
    [mutate],
  );

  return { cancelSlotBooking, loading, error };
}

export function useAddContentToCart() {
  const [mutate, { loading, error }] = useMutation(ADD_CONTENT_TO_CART);

  const addContentToCart = useCallback(
    (input: Record<string, any>) => {
      console.info("Adding content to cart with input:", input);
      return mutate({ variables: input });
    },
    [mutate],
  );

  return { addContentToCart, loading, error };
}

export function useSubmitRating() {
  const [mutate, { loading, error }] = useMutation(SUBMIT_RATING);

  const submitRating = useCallback(
    (input: Record<string, any>) => {
      console.info("Submitting rating with input:", input);
      return mutate({ variables: input });
    },
    [mutate],
  );

  return { submitRating, loading, error };
}

export function useRequestEventAttendance() {
  const [mutate, { loading, error }] = useMutation(REQUEST_EVENT_ATTENDANCE);

  const requestEventAttendance = useCallback(
    (data: Record<string, any>) => {
      console.info("Requesting event attendance with data:", data);
      return mutate({ variables: { data } });
    },
    [mutate],
  );

  return { requestEventAttendance, loading, error };
}

export const useUpdateEventArray = (id: any, field: any, value: any) => {
  const UPDATE_EVENT_ARRAY = gql`
      mutation updateEvent(
        $id: String!
      ) {
        updateEvent(data: {
          id: $id,
          eventType: PLATFORM_EVENT,
          ${field}: [${value.join(", ")}]
        })
      }
    `;

  const [mutate, { loading, error }] = useMutation(UPDATE_EVENT_ARRAY);

  const updateEventArray = useCallback(() => {
    console.info(
      "Updating event array with id:",
      id,
      "field:",
      field,
      "value:",
      value,
    );
    return mutate({ variables: { id } });
  }, [mutate]);

  return { updateEventArray, loading, error };
};

const EMPTY_KEYWORD_CATEGORIES: never[] = [];

export function useGetAllKeywordCategories(
  keywordType?: string,
  companyId?: string,
  sport?: string,
  skip = false,
) {
  const { data, loading, error, refetch } = useQuery<{
    getAllKeywordCategories: any;
  }>(GET_ALL_KEYWORD_CATEGORIES, {
    variables: { keywordType, companyId, sport },
    skip,
  });

  return {
    keywordCategories:
      data?.getAllKeywordCategories ?? EMPTY_KEYWORD_CATEGORIES,
    loading,
    error,
    refetch,
  };
}

export function useGetEventQrPreferences(eventId: string) {
  const { data, loading, error, refetch } = useQuery<{
    getEventQrPreferences: any;
  }>(GET_EVENT_QR_PREFERENCES, {
    variables: { eventId },
    skip: !eventId,
  });

  return {
    eventQrPreferences: data?.getEventQrPreferences ?? {},
    loading,
    error,
    refetch,
  };
}

export function useGetEventInvitation(invitationID: string) {
  const { data, loading, error, refetch } = useQuery<{
    getEventInvitation: any;
  }>(GET_EVENT_INVITATION, {
    variables: { invitationID },
  });

  return {
    eventInvitation: data?.getEventInvitation ?? null,
    loading,
    error,
    refetch,
  };
}

export function useGetLeagueInvitationByToken(token: string) {
  const { data, loading, error, refetch } = useQuery<{
    getLeagueInvitationByToken: any;
  }>(GET_LEAGUE_INVITATION_BY_TOKEN, {
    variables: { token },
    skip: !token,
    fetchPolicy: "network-only",
  });

  return {
    invitation: data?.getLeagueInvitationByToken ?? null,
    loading,
    error,
    refetch,
  };
}

export function useMyLeagueClubStatus(eventId: string) {
  const { data, loading, error } = useQuery<{
    getMyLeagueClubStatus: any[];
  }>(GET_MY_LEAGUE_CLUB_STATUS, {
    variables: { eventId },
    skip: !eventId,
    fetchPolicy: "network-only",
  });

  return {
    clubStatuses: data?.getMyLeagueClubStatus ?? [],
    loading,
    error,
  };
}

export function useMyCompanies(
  companyType?: number,
  excludeCompanyType?: number,
) {
  const { data, loading, error, refetch } = useQuery<{
    myCompanies: any;
  }>(MY_COMPANIES, {
    variables: { companyType, excludeCompanyType },
  });

  return {
    myCompanies: data?.myCompanies?.companyMemberships ?? [],
    loading,
    error,
    refetch,
  };
}

export function useEmployeesInCompany(data: any) {
  const {
    data: queryData,
    loading,
    error,
    refetch,
  } = useQuery<{
    employeesInCompany: any;
  }>(EMPLOYEES_IN_COMPANY, {
    variables: { data },
  });

  return {
    employeesInCompany: queryData?.employeesInCompany ?? [],
    loading,
    error,
    refetch,
  };
}

export function usePlatformEvents(data: any) {
  const {
    data: queryData,
    loading,
    error,
    refetch,
  } = useQuery<{
    myPlatformEvents: any;
  }>(GET_PLATFORM_EVENTS, {
    variables: {
      platformEventType: data?.platformEventType,
      isLeagueRequest: !!data?.platformEventType?.includes("_LEAGUE"),
    },
  });

  return {
    platformEvents: queryData?.myPlatformEvents ?? [],
    loading,
    error,
    refetch,
  };
}

export function useAcceptLeagueInvitation() {
  const [mutate, { loading, error }] = useMutation(ACCEPT_LEAGUE_INVITATION);

  const acceptLeagueInvitation = useCallback(
    (token: string) => mutate({ variables: { token } }),
    [mutate],
  );

  return { acceptLeagueInvitation, loading, error };
}

export function useIssueLeagueInviteToken() {
  const [mutate, { loading, error }] = useMutation(ISSUE_LEAGUE_INVITE_TOKEN);

  const issueLeagueInviteToken = useCallback(
    (invitationId: string) => mutate({ variables: { invitationId } }),
    [mutate],
  );

  return { issueLeagueInviteToken, loading, error };
}

export function useCompleteLeagueOnboarding() {
  const [mutate, { loading, error }] = useMutation(COMPLETE_LEAGUE_ONBOARDING);

  const completeLeagueOnboarding = useCallback(
    (
      eventId: string,
      clubCompanyId: string,
      officials: any[],
      userId: string,
    ) => {
      const input = { eventId, clubCompanyId, officials, userId };
      console.info("Completing league onboarding with input:", input);

      return mutate({ variables: { input } });
    },
    [mutate],
  );

  return {
    completeLeagueOnboarding,
    loading,
    error,
  };
}

export function useAddCompany() {
  const [mutate, { loading, error }] = useMutation(ADD_COMPANY);

  const addCompany = useCallback(
    (data: any) => {
      return mutate({ variables: { data } });
    },
    [mutate],
  );

  return {
    addCompany,
    loading,
    error,
  };
}

export function useCreateEvent() {
  const [mutate, { loading, error }] = useMutation(CREATE_EVENT);

  const createEvent = useCallback(
    (data: Record<string, unknown>) => {
      const variables = {
        name: data.name,
        description: data.description || "",
        platformEventType: data.platformEventType || "SPORTS_CLUB",
        eventType: "PLATFORM_EVENT",
      };
      return mutate({ variables });
    },
    [mutate],
  );

  return {
    createEvent,
    loading,
    error,
  };
}

export function useLeagueAllocation(eventId: string) {
  const { data, loading, error, refetch } = useQuery<{
    getLeagueAllocation: {
      eventId: string;
      isAdmin: boolean;
      teams: LeagueAllocationTeam[];
    };
  }>(GET_LEAGUE_ALLOCATION, {
    variables: { data: { eventId } },
    skip: !eventId,
    fetchPolicy: "cache-and-network",
  });

  return {
    allocation: data?.getLeagueAllocation ?? null,
    loading,
    error,
    refetch,
  };
}

export function useLeagueFixtures(eventId: string) {
  const { data, loading, error, refetch } = useQuery<{
    getLeagueFixtures: { eventId: string; fixtures: LeagueFixtureRecord[] };
  }>(GET_LEAGUE_FIXTURES, {
    variables: { data: { eventId } },
    skip: !eventId,
    fetchPolicy: "cache-and-network",
  });

  return {
    fixtures: data?.getLeagueFixtures.fixtures ?? [],
    loading,
    error,
    refetch,
  };
}

export function useClubLeagueFixtures(
  leagueEventId: string,
  clubEventId: string,
) {
  const { data, loading, error, refetch } = useQuery<{
    getClubLeagueFixtures: { fixtures: LeagueFixtureRecord[] };
  }>(GET_CLUB_LEAGUE_FIXTURES, {
    variables: { leagueEventId, clubEventId },
    skip: !leagueEventId || !clubEventId,
    fetchPolicy: "cache-and-network",
  });

  return {
    fixtures: data?.getClubLeagueFixtures.fixtures ?? [],
    loading,
    error,
    refetch,
  };
}

const LEAGUE_REFETCH = ["getLeagueAllocation", "getLeagueFixtures"];

export function useLeagueManagementActions() {
  const [setAllocationMutate, allocationState] = useMutation(
    SET_LEAGUE_TEAM_ALLOCATION,
    { refetchQueries: LEAGUE_REFETCH },
  );
  const [generateMutate, generateState] = useMutation(GENERATE_FIXTURES, {
    refetchQueries: LEAGUE_REFETCH,
  });
  const [updateMutate, updateState] = useMutation(UPDATE_LEAGUE_FIXTURE, {
    refetchQueries: LEAGUE_REFETCH,
  });
  const [publishMutate, publishState] = useMutation(PUBLISH_LEAGUE_FIXTURES, {
    refetchQueries: LEAGUE_REFETCH,
  });

  return {
    setTeamAllocation: useCallback(
      (data: {
        eventId: string;
        clusterId: string;
        competitionType: string;
        group?: string;
      }) => setAllocationMutate({ variables: { data } }),
      [setAllocationMutate],
    ),
    generateFixtures: useCallback(
      (data: {
        eventId: string;
        competitionType: string;
        group: string;
        clusterIds?: string[];
        startDate: string;
        endDate: string;
        optOutDates?: string[];
      }) => generateMutate({ variables: { data } }),
      [generateMutate],
    ),
    updateFixture: useCallback(
      (data: { fixtureId: string; startAt: string; location?: string }) =>
        updateMutate({ variables: { data } }),
      [updateMutate],
    ),
    publishFixtures: useCallback(
      (data: { eventId: string; competitionType: string; group?: string }) =>
        publishMutate({ variables: { data } }),
      [publishMutate],
    ),
    loading:
      allocationState.loading ||
      generateState.loading ||
      updateState.loading ||
      publishState.loading,
  };
}
